import { BENEFIT_FIELDS } from '../data/rulesEngineDefaults'
import { calculateAge, type PatientRecord } from '../data/patientData'
import type {
  BenefitFieldDef,
  BenefitInputs,
  BenefitRule,
  RuleOperator,
} from '../types/rulesEngine'

const NUMERIC_OPERATORS = new Set<RuleOperator>(['gt', 'gte', 'lt', 'lte'])

export function uniqueRuleFields(rules: BenefitRule[]): string[] {
  const seen = new Set<string>()
  const fields: string[] = []
  for (const rule of rules) {
    const key = rule.field?.trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    fields.push(key)
  }
  return fields
}

export function humanizeFieldKey(key: string): string {
  const trimmed = key.trim()
  if (!trimmed) return 'Field'
  const catalog = BENEFIT_FIELDS.find(
    (item) =>
      item.key.toLowerCase() === trimmed.toLowerCase() ||
      item.label.toLowerCase() === trimmed.toLowerCase()
  )
  if (catalog) return catalog.label

  return trimmed
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export function fieldKeyVariants(key: string): string[] {
  const trimmed = key.trim()
  if (!trimmed) return []
  const camel = trimmed
    .replace(/[-_\s]+(.)/g, (_, char: string) => char.toUpperCase())
    .replace(/^(.)/, (char) => char.toLowerCase())
  const pascal = camel.replace(/^(.)/, (char) => char.toUpperCase())
  const snake = trimmed
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase()
  const compact = trimmed.replace(/[\s_-]+/g, '')
  return [...new Set([trimmed, camel, pascal, snake, compact, compact.toLowerCase()])]
}

export function catalogFieldDef(fieldKey: string): BenefitFieldDef | undefined {
  const variants = fieldKeyVariants(fieldKey).map((item) => item.toLowerCase())
  const compact = fieldKey.replace(/[\s_-]+/g, '').toLowerCase()
  return BENEFIT_FIELDS.find((item) => {
    const key = item.key.toLowerCase()
    const label = item.label.toLowerCase()
    const labelCompact = item.label.replace(/[\s_$()/%-]+/g, '').toLowerCase()
    return (
      variants.includes(key) ||
      variants.includes(label) ||
      compact === key ||
      compact === labelCompact
    )
  })
}

type FieldMasterRef = { id: string; value_key: string; display_label: string }

export function canonicalFieldKey(fieldKey: string, masters?: FieldMasterRef[]): string {
  const trimmed = fieldKey.trim()
  if (!trimmed) return trimmed

  const master = masters?.find(
    (item) =>
      item.id === trimmed ||
      item.value_key.toLowerCase() === trimmed.toLowerCase() ||
      item.display_label.toLowerCase() === trimmed.toLowerCase()
  )
  const fromMaster = master?.value_key || trimmed
  const catalog =
    catalogFieldDef(fromMaster) ||
    catalogFieldDef(trimmed) ||
    (master ? catalogFieldDef(master.display_label) : undefined)
  return catalog?.key || fromMaster
}

export function canonicalizeRules(rules: BenefitRule[], masters?: FieldMasterRef[]): BenefitRule[] {
  return rules.map((rule) => ({
    ...rule,
    field: canonicalFieldKey(rule.field || rule.conditionFieldMasterId || '', masters),
  }))
}

export function lookupBenefitValue(
  inputs: BenefitInputs,
  fieldKey: string
): string | number | undefined {
  const catalog = catalogFieldDef(fieldKey)
  const variants = [
    ...fieldKeyVariants(fieldKey),
    ...(catalog ? fieldKeyVariants(catalog.key) : []),
    ...(catalog ? [catalog.key] : []),
  ]

  for (const variant of [...new Set(variants)]) {
    if (Object.prototype.hasOwnProperty.call(inputs, variant)) {
      const value = inputs[variant]
      if (value !== undefined && value !== null && value !== '') return value
      if (value === 0) return 0
    }
  }

  const lower = new Map(Object.keys(inputs).map((key) => [key.toLowerCase(), key]))
  for (const variant of [...new Set(variants)]) {
    const match = lower.get(variant.toLowerCase())
    if (!match) continue
    const value = inputs[match]
    if (value !== undefined && value !== null && value !== '') return value
    if (value === 0) return 0
  }
  return undefined
}

export function matchSelectValue(current: string, options: string[]): string {
  if (!current) return ''
  if (options.includes(current)) return current
  const compact = current.toLowerCase().replace(/[\s_-]+/g, '')
  const fuzzy = options.find((option) => option.toLowerCase().replace(/[\s_-]+/g, '') === compact)
  return fuzzy ?? current
}

export type DynamicRuleField = {
  key: string
  label: string
  type: 'select' | 'number' | 'text'
  options: string[]
}

export function buildDynamicRuleFields(
  rules: BenefitRule[],
  masters?: Array<{ id: string; value_key: string; display_label: string }>,
  fieldValuesByFieldId?: Record<string, Array<{ value_key: string; display_label: string }>>
): DynamicRuleField[] {
  return uniqueRuleFields(rules).map((key) => {
    const relatedRules = rules.filter((rule) => rule.field === key)
    const catalog = catalogFieldDef(key)
    const master = masters?.find(
      (item) =>
        item.value_key === key ||
        relatedRules.some((rule) => rule.conditionFieldMasterId === item.id)
    )
    const masterValues = master?.id ? fieldValuesByFieldId?.[master.id] ?? [] : []
    const numeric = relatedRules.some((rule) => NUMERIC_OPERATORS.has(rule.operator))
    const optionSet = new Set<string>()
    for (const option of catalog?.options ?? []) optionSet.add(option)
    for (const value of masterValues) {
      if (value.value_key) optionSet.add(value.value_key)
    }
    for (const rule of relatedRules) {
      if (rule.value) optionSet.add(rule.value)
    }

    const type: DynamicRuleField['type'] = catalog?.type === 'number' || numeric
      ? 'number'
      : optionSet.size > 0
        ? 'select'
        : 'text'

    return {
      key,
      label: master?.display_label || catalog?.label || humanizeFieldKey(key),
      type,
      options: [...optionSet],
    }
  })
}

export function formatDisplayValue(value: string | number | undefined): string {
  if (value === undefined || value === null || value === '') return '—'
  return String(value)
}

export function operatorPhrase(operator: RuleOperator): string {
  switch (operator) {
    case 'equals':
      return 'matches'
    case 'not_equals':
      return 'does not equal'
    case 'gt':
      return 'is greater than'
    case 'gte':
      return 'is at least'
    case 'lt':
      return 'is less than'
    case 'lte':
      return 'is at most'
    default:
      return 'compares to'
  }
}

export function buildRuleReason(args: {
  fieldLabel: string
  operator: RuleOperator
  expected: string
  actual: string
  passed: boolean
  failMessage?: string
}): string {
  const { fieldLabel, operator, expected, actual, passed, failMessage } = args
  if (passed) {
    return `Patient ${fieldLabel.toLowerCase()} (${actual}) ${operatorPhrase(operator)} the configured value (${expected}). Rule approved.`
  }
  if (failMessage?.trim()) {
    return failMessage.trim()
  }
  if (actual === '—') {
    return `No patient value was found for ${fieldLabel}, so it could not ${operatorPhrase(operator)} ${expected}. Rule not approved.`
  }
  return `Patient ${fieldLabel.toLowerCase()} is ${actual}, which does not ${operatorPhrase(operator)} the configured value ${expected}. Rule not approved.`
}

export function patientRecordToBenefitInputs(record: PatientRecord): BenefitInputs {
  const inputs: BenefitInputs = {}
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string' || typeof value === 'number') {
      inputs[key] = value
    }
  }
  const age = calculateAge(record.dateOfBirth)
  if (age > 0) {
    inputs.patientAge = age
    inputs.age = age
  }
  return inputs
}
